/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import {
    FormControl,
    FormHelperText,
} from 'material-ui/Form';
import Input, { InputLabel } from 'material-ui/Input';
import Select from 'material-ui/Select';
import Grid from 'material-ui/Grid';
import { MenuItem } from 'material-ui/Menu';
import NumberFormat from 'react-number-format';
import moment from 'moment';
import appSettings from '../../../../config/appSettings';
import CurrenyFormat from '../../../common/CurrenyFormat';
import SectionTitle from '../../../common/components/Typography/SectionTitle';
import ContinueButton from '../../../common/components/Buttons/ContinueButton';

const terms = [3, 6, 9].concat(Array.from(Array(35).keys()).map(item => (item + 1) * 12));
const styles = ({ spacing: { unit } }) => ({
    root: {
        padding: unit * 2,
        paddingTop: 0,
    },
    formControl: {
        marginTop: unit * 3,
        minWidth: unit * 15,
        width: '100%',
        '&:not(:last-of-type)': {
            marginRight: unit * 3,
        },
    },
    sectionTitle: {
        marginLeft: '16px',
    },
});

function RequestedLoan(props) {
    const hasApplicatnsIncome = () => props.applicantsIncome.every(item => item.amount > 0);
    const show = appSettings.onlyOneApplicant
        || (props.applicationType === 'ltd' && props.mortgageType)
        || (props.mortgageType && hasApplicatnsIncome());
    const maxAmountPossible = (
        props.estaminatedPropertyValue.amount * (1 - appSettings.minimumDepositRatio) || 0
    );
    const requestedLoanAmountError = (
        !!props.estaminatedPropertyValue.amount
        && Number(props.requestedLoanAmount.amount) > maxAmountPossible
    );
    const getProducts = () => {
        props.setStepCompleted(4, false);
        props.setStep(1);
        props.setStepCompleted(0, true);
        if (!props.steps[1].completed) {
            props.getQuickQuote(props).then((data) => {
                props.setStepCompleted(1, true);
                if (!props.applicationId) {
                    props.setApplicationId(data.value.applicationId);
                }
            });
        }
    };
    const formCompleted = (
        show
        && props.mortgageType
        && Number(props.estaminatedPropertyValue.amount) > 0
        && Number(props.requestedLoanAmount.amount) > 0
        && Number(props.estaminatedPropertyMonthlyRent.amount) > 0
        && props.requestedTimeSpan
        && Number(props.applicantsIncome[0].amount) > 0
        && !requestedLoanAmountError
    );
    const setEstaminatedPropertyValue = (event) => {
        props.setEstaminatedPropertyValue(event);
        props.setStepsUncompleted(0);
    };
    const setEstaminatedPropertyMonthlyRent = (event) => {
        props.setEstaminatedPropertyMonthlyRent(event);
        props.setStepsUncompleted(0);
    };
    const setRequestedTimeSpan = (event) => {
        props.setRequestedTimeSpan(event);
        props.setStepsUncompleted(0);
    };
    const setApplicantsIncome = (index, event) => {
        props.setApplicantsIncome(index, event);
        props.setStepsUncompleted(0);
    };
    const setRequestedLoanAmount = (event) => {
        props.setRequestedLoanAmount(event);
        props.setStepsUncompleted(0);
    };
    const applicantIncomeInput = appSettings.onlyOneApplicant
        ? (
            <Grid
                item
                xs={12}
                sm={6}
                md={4}
            >
                <FormControl
                    className={props.classes.formControl}
                >
                    <InputLabel htmlFor="income">
                        Applicant Taxable Income
                    </InputLabel>
                    <Input
                        id="income"
                        value={props.applicantsIncome[0].amount}
                        onChange={event => setApplicantsIncome(0, event)}
                        inputComponent={CurrenyFormat}
                    />
                </FormControl>
            </Grid>
        )
        : '';
    return show
        ? (
            <div>
                <div className={props.classes.sectionTitle}>
                    <SectionTitle>
                        Finance details
                    </SectionTitle>
                </div>
                <div className={props.classes.root}>
                    <Grid
                        container
                        spacing={16}
                    >
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                        >
                            <FormControl className={props.classes.formControl}>
                                <InputLabel htmlFor="estaminatedPropertyValue">
                                    Estimated property value
                                </InputLabel>
                                <Input
                                    id="estaminatedPropertyValue"
                                    value={props.estaminatedPropertyValue.amount}
                                    onChange={event => setEstaminatedPropertyValue(event)}
                                    inputComponent={CurrenyFormat}
                                />
                            </FormControl>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                        >
                            <FormControl className={props.classes.formControl}>
                                <InputLabel htmlFor="estaminatedPropertyMonthlyRent">
                                    Estimated monthly rent
                                </InputLabel>
                                <Input
                                    id="estaminatedPropertyMonthlyRent"
                                    value={props.estaminatedPropertyMonthlyRent.amount}
                                    onChange={event => setEstaminatedPropertyMonthlyRent(event)}
                                    inputComponent={CurrenyFormat}
                                />
                            </FormControl>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                        >
                            <FormControl className={props.classes.formControl}>
                                <InputLabel htmlFor="requested-time-span">
                                    Mortgage term
                                </InputLabel>
                                <Select
                                    value={props.requestedTimeSpan}
                                    onChange={event => setRequestedTimeSpan(event)}
                                    inputProps={{
                                        name: 'requestedTimeSpan',
                                        id: 'requested-time-span',
                                    }}
                                >
                                    {(
                                        terms.map(item => (
                                            <MenuItem
                                                value={item}
                                                key={item}
                                            >
                                                {moment.duration(item, 'months').humanize()}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        {applicantIncomeInput}
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                        >
                            <FormControl
                                className={props.classes.formControl}
                                error={requestedLoanAmountError}
                                aria-describedby="requestedLoanAmount-max"
                            >
                                <InputLabel htmlFor="requestedLoanAmount">
                                    {(
                                        props.mortgageType === 'remortgage_current_balance'
                                            ? 'Current loan balance'
                                            : 'Requested loan amount'
                                    )}
                                </InputLabel>
                                <Input
                                    id="requestedLoanAmount"
                                    value={props.requestedLoanAmount.amount}
                                    onChange={event => setRequestedLoanAmount(event)}
                                    inputComponent={CurrenyFormat}
                                />
                                <FormHelperText id="requestedLoanAmount-max">
                                    Max possible: <NumberFormat
                                    value={maxAmountPossible}
                                    displayType="text"
                                    thousandSeparator
                                    prefix="&pound;"
                                />
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>
                </div>
                <div className={props.classes.root}>
                    <ContinueButton
                        disabled={!formCompleted}
                        onClick={() => getProducts()}
                    />
                </div>
            </div>
        )
        : '';
}

RequestedLoan.propTypes = {
    classes: PropTypes.object.isRequired,
    applicationId: PropTypes.string.isRequired,
    applicationType: PropTypes.string.isRequired,
    mortgageType: PropTypes.string.isRequired,
    applicantsIncome: PropTypes.array.isRequired,
    estaminatedPropertyValue: PropTypes.object.isRequired,
    estaminatedPropertyMonthlyRent: PropTypes.object.isRequired,
    requestedLoanAmount: PropTypes.object.isRequired,
    requestedTimeSpan: PropTypes.number.isRequired,
    customer: PropTypes.object.isRequired,
    steps: PropTypes.array.isRequired,
    setStep: PropTypes.func.isRequired,
    setStepCompleted: PropTypes.func.isRequired,
    setStepsUncompleted: PropTypes.func.isRequired,
    setEstaminatedPropertyValue: PropTypes.func.isRequired,
    setEstaminatedPropertyMonthlyRent: PropTypes.func.isRequired,
    setRequestedLoanAmount: PropTypes.func.isRequired,
    setRequestedTimeSpan: PropTypes.func.isRequired,
    setApplicantsIncome: PropTypes.func.isRequired,
    getQuickQuote: PropTypes.func.isRequired,
    setApplicationId: PropTypes.func.isRequired,
};

export default withStyles(styles)(RequestedLoan);



// WEBPACK FOOTER //
// ./src/app/FormStepper/QuickQuote/components/RequestedLoan.js